/* -------------------------------------------------------
   ChatMessage — Renders a single user or assistant message.
   Memoized with a shallow comparison on message fields to
   avoid re-renders during polling of other messages.
   ------------------------------------------------------- */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AnswerMetaPanels from './AnswerMetaPanels';
import ActionableTradeTable from './ActionableTradeTable';
import type { ChatMessage as MessageType } from '../../types/chatV2';

interface Props {
  message: MessageType;
  showDebug: boolean;
  onFollowup: (text: string) => void;
  onStop: (id: string) => void;
  onRetry: (id: string) => void;
  userInitial: string;
  username?: string | null;
}

/**
 * Insert commas into numbers in markdown text.
 * - Handles $12345.67 → $12,345.67 and plain 112000 → 112,000
 * - Skips 4-digit numbers that look like years (1900–2099 when not
 *   preceded by $ or part of a larger number).
 */
/**
 * Remove the "Alternatives and Their Current Prices" section from markdown
 * so we don't show that table (alternatives are in the TLH actionable table + confirmation).
 */
function stripAlternativesPricesTable(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let skip = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^#+\s*Alternatives and Their Current Prices\s*$/i.test(line.trim())) {
      skip = true;
      continue;
    }
    if (skip) {
      if (/^#+\s/.test(line.trim())) skip = false;
      else continue;
    }
    out.push(line);
  }
  return out.join('\n');
}

function formatNumbers(text: string): string {
  // Regex: optional leading $ or -, then digits, optional decimal part
  // Captures the full numeric token including optional $ prefix
  return text.replace(
    /(\$-?|\b-?)(\d+)(\.\d+)?\b/g,
    (match, prefix: string, digits: string, decimals: string | undefined) => {
      // Skip 4-digit standalone numbers that look like years (no $ prefix)
      if (
        !prefix.includes('$') &&
        digits.length === 4 &&
        !decimals &&
        /^(19|20)\d{2}$/.test(digits)
      ) {
        return match;
      }
      // Add commas to the integer part
      const withCommas = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${prefix}${withCommas}${decimals ?? ''}`;
    },
  );
}

/* ---- typewriter hook ---- */

/**
 * Progressively reveals `text` with an adaptive typewriter effect.
 * - `animate` = false → returns full text immediately (historical msgs).
 * - When `text` grows (new partial / final data from polling), the
 *   reveal cursor chases the new length automatically.
 * - Adaptive chunk size: short answers feel deliberate, long answers
 *   stream fast so the user isn't kept waiting.
 */
function useTypewriter(
  text: string,
  animate: boolean,
): { displayed: string; isTyping: boolean } {
  const [revealed, setRevealed] = useState(() =>
    animate ? 0 : text.length,
  );

  // If text clears (retry / reset), restart the cursor
  const prevLenRef = useRef(text.length);
  useEffect(() => {
    if (text.length === 0 && prevLenRef.current > 0) {
      setRevealed(0);
    }
    prevLenRef.current = text.length;
  }, [text.length]);

  // If not animating, always show everything
  useEffect(() => {
    if (!animate) setRevealed(text.length);
  }, [animate, text.length]);

  // Animation tick — runs whenever there's unrevealed text
  useEffect(() => {
    if (!animate || revealed >= text.length) return;

    // Adaptive chunk: bigger chunks for longer remaining text
    const remaining = text.length - revealed;
    const chunk = remaining > 3000 ? 35 : remaining > 1000 ? 18 : remaining > 300 ? 10 : 4;

    const timer = setTimeout(() => {
      setRevealed((prev) => Math.min(prev + chunk, text.length));
    }, 18); // ~55 ticks/sec, smooth without being wasteful

    return () => clearTimeout(timer);
  }, [revealed, text.length, animate]);

  return {
    displayed: text.slice(0, revealed),
    isTyping: animate && revealed < text.length,
  };
}

const STATUS_LABEL: Record<string, string> = {
  QUEUED: 'Preparing your answer\u2026',
  RUNNING: 'Analyzing\u2026',
  PARTIAL: 'Processing\u2026',
};

const ChatMessageComponent: React.FC<Props> = ({
  message,
  showDebug,
  onFollowup,
  onStop,
  onRetry,
  userInitial,
  username,
}) => {
  const {
    role,
    content,
    jobStatus,
    progressPct,
    followups,
    error,
    stoppedByUser,
  } = message;

  const isUser = role === 'user';
  const isActive =
    !!jobStatus &&
    !['COMPLETE', 'FAILED'].includes(jobStatus) &&
    !stoppedByUser;
  const isFailed = jobStatus === 'FAILED' && !stoppedByUser;
  const showProgressBar =
    isActive && (jobStatus !== 'QUEUED' || (progressPct ?? 0) > 0);

  // Typewriter: animate only for assistant messages that started empty
  // (i.e. new messages created this session, not loaded from history).
  const mountContentRef = useRef(content);
  const shouldAnimate = role === 'assistant' && mountContentRef.current === '';

  const { displayed: visibleContent, isTyping } = useTypewriter(
    content,
    shouldAnimate,
  );

  // Show follow-ups & meta only after the typewriter finishes
  const doneTyping = !isTyping && jobStatus === 'COMPLETE';

  // Track which TLH tickers are selected so replacement table only enables those dropdowns
  const [selectedTlhTickers, setSelectedTlhTickers] = useState<Set<string>>(new Set());
  const onTlhSelectionChange = useCallback((tickers: string[]) => {
    setSelectedTlhTickers(new Set(tickers));
  }, []);

  return (
    <div className={`cv2-msg cv2-msg--${role}`}>
      <div className="cv2-msg-row">
        {/* AI avatar */}
        {!isUser && (
          <div className="cv2-avatar cv2-avatar--assistant" aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
        )}

        {/* Message content */}
        <div className={`cv2-content cv2-content--${role}`}>
          {/* ---- USER ---- */}
          {isUser && <p className="cv2-user-text">{content}</p>}

          {/* ---- ASSISTANT ---- */}
          {!isUser && (
            <>
              {/* Typing dots (QUEUED with no content yet) */}
              {isActive && !content && (
                <div className="cv2-typing" aria-label="AI is thinking">
                  <span className="cv2-typing-dot" />
                  <span className="cv2-typing-dot" />
                  <span className="cv2-typing-dot" />
                </div>
              )}

              {/* Progress indicator */}
              {isActive && (
                <div className="cv2-progress">
                  <span className="cv2-progress-label">
                    {STATUS_LABEL[jobStatus!] || 'Working\u2026'}
                    {(progressPct ?? 0) > 0 && ` ${progressPct}%`}
                  </span>
                  <div className="cv2-progress-bar">
                    <div
                      className={`cv2-progress-fill${
                        !showProgressBar
                          ? ' cv2-progress-fill--indeterminate'
                          : ''
                      }`}
                      style={
                        showProgressBar ? { width: `${progressPct}%` } : undefined
                      }
                    />
                  </div>
                </div>
              )}

              {/* Markdown answer (typewriter-revealed) */}
              {visibleContent && (
                <div className="cv2-markdown">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, ...props }: any) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                      table: ({ node, ...props }: any) => (
                        <div className="cv2-table-wrap">
                          <table {...props} />
                        </div>
                      ),
                    }}
                  >
                    {formatNumbers(stripAlternativesPricesTable(visibleContent))}
                  </ReactMarkdown>
                  {isTyping && <span className="cv2-cursor" aria-hidden="true" />}
                </div>
              )}

              {/* Stopped by user */}
              {stoppedByUser && (
                <div className="cv2-stopped">
                  <span>Generation stopped</span>
                  <button
                    className="cv2-retry-btn"
                    onClick={() => onRetry(message.id)}
                    aria-label="Retry this message"
                  >
                    &#8635; Retry
                  </button>
                </div>
              )}

              {/* Error / Failed */}
              {isFailed && (
                <div className="cv2-error-box">
                  <span>{error || 'Something went wrong.'}</span>
                  <button
                    className="cv2-retry-btn"
                    onClick={() => onRetry(message.id)}
                    aria-label="Retry this message"
                  >
                    &#8635; Retry
                  </button>
                </div>
              )}

              {/* Client-side stop button */}
              {isActive && (
                <button
                  className="cv2-stop-btn"
                  onClick={() => onStop(message.id)}
                  aria-label="Stop generating"
                >
                  &#9632; Stop
                </button>
              )}

              {/* Follow-up suggestion chips (after typewriter finishes) */}
              {doneTyping &&
                followups &&
                followups.length > 0 && (
                  <div
                    className="cv2-followups"
                    role="list"
                    aria-label="Follow-up suggestions"
                  >
                    {followups.map((f, i) => (
                      <button
                        key={i}
                        className="cv2-followup-btn"
                        role="listitem"
                        onClick={() => onFollowup(f)}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}

              {/* Actionable trade tables (show after full message is displayed / typewriter done) */}
              {doneTyping &&
                message.actionableItems &&
                message.actionableItems.length > 0 &&
                (() => {
                  const items = message.actionableItems;
                  const tlhTable = items.find((t) => t.type === 'TAX_LOSS_HARVEST');
                  const replacementTable = items.find((t) => t.type === 'REPLACEMENT_BUY');
                  if (tlhTable && replacementTable) {
                    return (
                      <ActionableTradeTable
                        key={tlhTable.id}
                        table={tlhTable}
                        replacementTable={replacementTable}
                        username={username ?? 'demo'}
                      />
                    );
                  }
                  return items.map((t) => (
                    <ActionableTradeTable
                      key={t.id}
                      table={t}
                      username={username ?? 'demo'}
                      onTlhSelectionChange={t.type === 'TAX_LOSS_HARVEST' ? onTlhSelectionChange : undefined}
                      enabledReplacementTickers={t.type === 'REPLACEMENT_BUY' ? selectedTlhTickers : undefined}
                    />
                  ));
                })()}

              {/* Data-transparency panels (after typewriter finishes) */}
              {doneTyping && (
                <AnswerMetaPanels
                  dataSources={message.dataSources}
                  assumptions={message.assumptions}
                  confidence={message.confidence}
                  debug={message.debug}
                  showDebug={showDebug}
                />
              )}
            </>
          )}
        </div>

        {/* User avatar */}
        {isUser && (
          <div className="cv2-avatar cv2-avatar--user" aria-hidden="true">
            {userInitial}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Custom memo comparison — only re-render when visible data changes.
 * This avoids re-rendering all messages on every poll tick.
 */
export default React.memo(ChatMessageComponent, (prev, next) => {
  const pm = prev.message;
  const nm = next.message;
  return (
    pm.id === nm.id &&
    pm.content === nm.content &&
    pm.jobStatus === nm.jobStatus &&
    pm.progressPct === nm.progressPct &&
    pm.stoppedByUser === nm.stoppedByUser &&
    pm.error === nm.error &&
    pm.followups === nm.followups &&
    pm.dataSources === nm.dataSources &&
    pm.assumptions === nm.assumptions &&
    pm.confidence === nm.confidence &&
    pm.debug === nm.debug &&
    pm.actionableItems === nm.actionableItems &&
    prev.showDebug === next.showDebug &&
    prev.userInitial === next.userInitial &&
    prev.username === next.username
  );
});
